/**
 * Canvas glitch renderer for <Image>.
 *
 * Pipeline per frame:
 *   src  = image (or its shatter shards on hover)
 *   buf  = src + pixel-level corruption (torn slices, mosaic blocks, inverts)
 *   main = buf split into R/G/B and recomposited at offsets (chromatic
 *          aberration)
 *
 * Everything is drawn in CSS pixels: each canvas is allocated at
 * size * devicePixelRatio and given a matching base transform. Source
 * rectangles read out of another canvas are the exception - those are in that
 * canvas' own device pixels, hence the `d` multipliers.
 */

const DPR_CAP = 2;
const TAU = Math.PI * 2;
/**
 * Constant overscan on the image, in CSS px. Gives the facet parallax
 * somewhere to sample from without the framing ever changing - a bleed that
 * scaled with hover would read as the image zooming in when it breaks.
 */
const EDGE_BLEED = 10;
/**
 * How long one corruption pattern holds before it is re-rolled. Everything
 * random per frame - slices, blocks, the aberration jitter - is keyed to this,
 * so the whole effect ticks at one deliberate rate rather than strobing.
 */
const GLITCH_HOLD_MS = 150;

export interface GlitchInput {
  /** ambient energy from scroll speed / position in viewport, 0..1 */
  intensity: number;
  /** eased hover progress, 0..1 */
  hover: number;
  /** short random corruption burst, 0..1 */
  burst: number;
}

interface Shard {
  path: Path2D;
  cx: number;
  cy: number;
  /** unit vector pointing away from the impact point */
  nx: number;
  ny: number;
  /** travel distance at full hover */
  dist: number;
  rot: number;
  /** 0..0.4, staggers the shards so they don't all leave at once */
  delay: number;
  /** per-facet brightness offset, -0.07..0.09 */
  shade: number;
  /** parallax offset of the image seen through this facet, in CSS px */
  refX: number;
  refY: number;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function mulberry32(a: number) {
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(v: number, lo: number, hi: number) {
  return v < lo ? lo : v > hi ? hi : v;
}

function ctxOf(c: HTMLCanvasElement) {
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  return ctx;
}

function makeCanvas(w = 1, h = 1) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

export class GlitchFX {
  private ctx: CanvasRenderingContext2D;

  /** clean composition (image / shards) */
  private src = makeCanvas();
  private sctx = ctxOf(this.src);
  /** corrupted composition, fed to the channel split */
  private buf = makeCanvas();
  private bctx = ctxOf(this.buf);
  /** single isolated colour channel of `buf` */
  private tint = makeCanvas();
  private tctx = ctxOf(this.tint);
  /** scratch used to downsample mosaic blocks */
  private px = makeCanvas(256, 256);
  private pctx = ctxOf(this.px);

  private w = 0;
  private h = 0;
  private d = 1;

  private shards: Shard[] = [];
  private impactX = 0;
  private impactY = 0;

  constructor(
    private canvas: HTMLCanvasElement,
    private img: HTMLImageElement,
    private seed: number
  ) {
    this.ctx = ctxOf(canvas);
    this.pctx.imageSmoothingEnabled = true;
  }

  get ready() {
    return this.img.naturalWidth > 0 && this.w > 0 && this.h > 0;
  }

  resize(cssW: number, cssH: number) {
    const d = Math.min(DPR_CAP, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(cssW));
    const h = Math.max(1, Math.round(cssH));
    if (w === this.w && h === this.h && d === this.d) return;
    this.w = w;
    this.h = h;
    this.d = d;

    const W = Math.round(w * d);
    const H = Math.round(h * d);
    for (const c of [this.canvas, this.src, this.buf, this.tint]) {
      c.width = W;
      c.height = H;
    }
    for (const c of [this.ctx, this.sctx, this.bctx, this.tctx]) {
      c.setTransform(d, 0, 0, d, 0, 0);
    }

    // geometry is in CSS px, so it has to be rebuilt at the new size
    this.shards = [];
  }

  /** (re)break the image, cracks radiating from the given point */
  shatter(x: number, y: number) {
    this.impactX = clamp(x, 0, this.w);
    this.impactY = clamp(y, 0, this.h);
    this.shards = this.buildShards();
  }

  destroy() {
    for (const c of [this.src, this.buf, this.tint, this.px]) {
      c.width = 0;
      c.height = 0;
    }
    this.shards = [];
  }

  /** returns false while the image has nothing to draw yet */
  render(now: number, input: GlitchInput) {
    if (!this.ready) return false;
    const { w, h } = this;

    const energy = clamp(
      input.intensity * 0.75 + input.burst + input.hover * 0.18,
      0,
      1
    );
    const epoch = Math.floor(now / GLITCH_HOLD_MS);
    const rnd = mulberry32((this.seed ^ Math.imul(epoch, 0x9e3779b1)) >>> 0);

    // 1. clean plate -------------------------------------------------------
    this.sctx.clearRect(0, 0, w, h);
    if (input.hover > 0.002) this.drawShards(input.hover);
    else this.drawCover(this.sctx, EDGE_BLEED);

    // 2. pixel-level corruption -------------------------------------------
    this.bctx.clearRect(0, 0, w, h);
    this.bctx.drawImage(this.src, 0, 0, w, h);
    if (energy > 0.02) this.corrupt(epoch, now, energy);

    // 3. chromatic aberration ---------------------------------------------
    const ctx = this.ctx;
    ctx.clearRect(0, 0, w, h);
    const ab = energy * 7.5 + input.hover * 1.5 + energy * rnd() * 4;
    if (ab < 0.3) {
      ctx.drawImage(this.buf, 0, 0, w, h);
    } else {
      const ay = (rnd() - 0.5) * ab * 0.6;
      // every channel is also blown up by `ab` on each side, so shifting one
      // never uncovers a bare strip along the edge of the canvas
      const bw = w + ab * 2;
      const bh = h + ab * 2;
      const put = (color: string, dx: number, dy: number) => {
        this.channel(color);
        ctx.drawImage(this.tint, dx - ab, dy - ab, bw, bh);
      };
      ctx.globalCompositeOperation = 'lighter';
      put('#f00', ab, ay);
      put('#0f0', 0, 0);
      put('#00f', -ab, -ay);
      ctx.globalCompositeOperation = 'source-over';
    }

    return true;
  }

  // --- composition helpers -----------------------------------------------

  private drawCover(ctx: CanvasRenderingContext2D, bleed = 0) {
    const { img, w, h } = this;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const dw = w + bleed * 2;
    const dh = h + bleed * 2;
    const scale = Math.max(dw / iw, dh / ih);
    const sw = dw / scale;
    const sh = dh / scale;
    ctx.drawImage(
      img,
      (iw - sw) / 2,
      (ih - sh) / 2,
      sw,
      sh,
      -bleed,
      -bleed,
      dw,
      dh
    );
  }

  /** isolate one colour channel of `buf` into `tint`, alpha preserved */
  private channel(color: string) {
    const t = this.tctx;
    const W = this.tint.width;
    const H = this.tint.height;
    t.setTransform(1, 0, 0, 1, 0, 0);
    t.clearRect(0, 0, W, H);
    t.drawImage(this.buf, 0, 0);
    t.globalCompositeOperation = 'multiply';
    t.fillStyle = color;
    t.fillRect(0, 0, W, H);
    t.globalCompositeOperation = 'destination-in';
    t.drawImage(this.buf, 0, 0);
    t.globalCompositeOperation = 'source-over';
    t.setTransform(this.d, 0, 0, this.d, 0, 0);
  }

  // --- pixel corruption ---------------------------------------------------

  private corrupt(epoch: number, now: number, energy: number) {
    const { w, h, d, bctx } = this;
    const rnd = mulberry32((this.seed ^ Math.imul(epoch, 2654435761)) >>> 0);

    // slowly drifting band - slices inside it tear much further
    const band = ((now * 0.02 + (this.seed % 997)) % (h + 160)) - 80;
    const bandH = 24 + h * 0.12;

    const count = 1 + Math.round(energy * 5);
    for (let i = 0; i < count; i++) {
      // biased small: mostly 1-4px scanline tears, occasionally a fat block
      const sh = Math.max(1, Math.round(rnd() * rnd() * h * 0.18) + 1);
      const sy = Math.floor(rnd() * (h - sh));
      const inBand = sy + sh > band && sy < band + bandH;
      const reach = energy * w * (inBand ? 0.16 : 0.06);
      const dx = Math.round((rnd() - 0.5) * 2 * reach);
      const mode = rnd();

      if (mode < 0.07) {
        // mosaic: downsample a block and blow it back up unsmoothed
        const bw = Math.max(8, Math.round((0.25 + rnd() * 0.75) * w));
        const bx = Math.floor(rnd() * (w - bw));
        // clamped so the downsampled block always fits the scratch canvas
        const f = Math.max(3 + Math.floor(rnd() * 10), bw / this.px.width);
        const pw = Math.max(1, Math.round(bw / f));
        const ph = Math.max(1, Math.round(sh / f));
        this.pctx.clearRect(0, 0, pw, ph);
        this.pctx.drawImage(
          this.src,
          bx * d,
          sy * d,
          bw * d,
          sh * d,
          0,
          0,
          pw,
          ph
        );
        bctx.imageSmoothingEnabled = false;
        bctx.drawImage(this.px, 0, 0, pw, ph, bx + dx, sy, bw, sh);
        bctx.imageSmoothingEnabled = true;
      } else if (mode < 0.1) {
        // dropped scanline - invert it
        bctx.save();
        bctx.globalCompositeOperation = 'difference';
        bctx.fillStyle = '#fff';
        bctx.fillRect(0, sy, w, sh);
        bctx.restore();
      } else {
        // torn slice, sometimes sampled from a different row (datamosh)
        const from = rnd() < 0.25 ? Math.floor(rnd() * (h - sh)) : sy;
        bctx.drawImage(
          this.src,
          0,
          from * d,
          w * d,
          sh * d,
          dx,
          sy,
          w,
          sh
        );
        if (rnd() < 0.2) {
          // per-slice colour break on top of the global aberration
          bctx.save();
          bctx.globalCompositeOperation = 'lighter';
          bctx.globalAlpha = 0.25;
          bctx.drawImage(
            this.src,
            0,
            from * d,
            w * d,
            sh * d,
            dx + (rnd() < 0.5 ? -4 : 4),
            sy,
            w,
            sh
          );
          bctx.restore();
        }
      }
    }
  }

  // --- shatter ------------------------------------------------------------

  private buildShards(): Shard[] {
    const { w, h, impactX: ix, impactY: iy } = this;
    const rnd = mulberry32(
      (this.seed ^ Math.imul(Math.round(ix) + 1, 73856093) ^
        Math.imul(Math.round(iy) + 1, 19349663)) >>>
        0
    );
    const maxR = Math.max(
      Math.hypot(ix, iy),
      Math.hypot(w - ix, iy),
      Math.hypot(ix, h - iy),
      Math.hypot(w - ix, h - iy)
    );
    const spokes = 5 + Math.floor(rnd() * 3);
    const rings = [0, 0.28, 0.66, 1.08];

    const angles: number[] = [];
    for (let i = 0; i < spokes; i++) {
      angles.push((i / spokes) * TAU + ((rnd() - 0.5) * TAU) / spokes / 1.5);
    }
    // shared radius jitter so neighbouring shards keep matching edges
    const jitter = rings.map((_, j) =>
      angles.map(() =>
        j === rings.length - 1 ? 1.02 + rnd() * 0.3 : 0.78 + rnd() * 0.45
      )
    );
    const px = (i: number, j: number) =>
      ix + Math.cos(angles[i % spokes]!) * rings[j]! * maxR * jitter[j]![i % spokes]!;
    const py = (i: number, j: number) =>
      iy + Math.sin(angles[i % spokes]!) * rings[j]! * maxR * jitter[j]![i % spokes]!;

    const shards: Shard[] = [];
    for (let j = 0; j < rings.length - 1; j++) {
      for (let i = 0; i < spokes; i++) {
        const pts =
          j === 0
            ? [ix, iy, px(i, 1), py(i, 1), px(i + 1, 1), py(i + 1, 1)]
            : [
                px(i, j),
                py(i, j),
                px(i + 1, j),
                py(i + 1, j),
                px(i + 1, j + 1),
                py(i + 1, j + 1),
                px(i, j + 1),
                py(i, j + 1),
              ];

        const path = new Path2D();
        let cx = 0;
        let cy = 0;
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (let p = 0; p < pts.length; p += 2) {
          const x = pts[p]!;
          const y = pts[p + 1]!;
          if (p === 0) path.moveTo(x, y);
          else path.lineTo(x, y);
          cx += x;
          cy += y;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
        path.closePath();
        const n = pts.length / 2;
        cx /= n;
        cy /= n;

        const len = Math.hypot(cx - ix, cy - iy) || 1;
        const tang = (rnd() - 0.5) * 3.5;
        shards.push({
          path,
          cx,
          cy,
          nx: (cx - ix) / len,
          ny: (cy - iy) / len,
          dist: (0.5 + j * 0.8) * (0.5 + rnd() * 0.8),
          rot: (rnd() - 0.5) * 0.018,
          delay: rnd() * 0.35,
          shade: (rnd() - 0.45) * 0.16,
          // a tilted pane of glass shifts what you see through it. The shift
          // is mostly radial - the pane is bowing out from the impact - with a
          // little sideways play, and small enough to stay believable.
          refX: ((cx - ix) / len) * (1.5 + rnd() * 5) - ((cy - iy) / len) * tang,
          refY: ((cy - iy) / len) * (1.5 + rnd() * 5) + ((cx - ix) / len) * tang,
          minX,
          minY,
          maxX,
          maxY,
        });
      }
    }
    return shards;
  }

  private drawShards(hover: number) {
    const { sctx, w, h } = this;
    if (!this.shards.length) this.shards = this.buildShards();

    for (const s of this.shards) {
      const p = clamp((hover - s.delay) / (1 - s.delay), 0, 1);
      // ease-out-back: shards overshoot slightly then settle
      const e = 1 + 2.2 * Math.pow(p - 1, 3) + 1.2 * Math.pow(p - 1, 2);
      const ox = s.nx * s.dist * e;
      const oy = s.ny * s.dist * e;
      const pad = s.dist + 4;
      if (
        s.maxX + ox < -pad ||
        s.minX + ox > w + pad ||
        s.maxY + oy < -pad ||
        s.minY + oy > h + pad
      ) {
        continue;
      }
      sctx.save();
      sctx.translate(s.cx + ox, s.cy + oy);
      sctx.rotate(s.rot * e);
      sctx.translate(-s.cx, -s.cy);
      sctx.clip(s.path);

      // the facet is a tilted pane: what you see through it is displaced, but
      // not resized or rotated. Clipped first, so only the content shifts -
      // the fragment itself stays put.
      sctx.save();
      sctx.translate(s.refX * e, s.refY * e);
      this.drawCover(sctx, EDGE_BLEED);
      sctx.restore();

      if (e > 0.02) {
        // facets catch the light slightly differently
        const a = Math.abs(s.shade) * e;
        sctx.globalCompositeOperation = s.shade > 0 ? 'lighter' : 'source-over';
        sctx.fillStyle =
          s.shade > 0 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
        sctx.fill(s.path);
        sctx.globalCompositeOperation = 'source-over';
        // still clipped, so a wide stroke only lays down its inner half: a
        // bevel catching the light along the broken edge, plus a hairline
        sctx.lineWidth = 4;
        sctx.strokeStyle = `rgba(255,255,255,${(0.13 * e).toFixed(3)})`;
        sctx.stroke(s.path);
        sctx.lineWidth = 0.75;
        sctx.strokeStyle = `rgba(255,255,255,${(0.2 * e).toFixed(3)})`;
        sctx.stroke(s.path);
      }
      sctx.restore();
    }
  }
}
